import ControlledEmailTextField from "@/components/common/form/ControlledEmailTextField";
import { ForgottenPasswordActionButton } from "@/components/forgotten-password-components/ForgottenPasswordLayout";
import LazoModal from "@/components/utils/LazoModal";
import { Colors } from "@/enums/Colors";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import LazoCustomAuthControllerV2Service from "@/services/LazoCustomAuthControllerV2Service";
import { Box, Link, Typography } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import CheckYourEmailSVG from "../../../assets/images/check-your-email-icon.svg";
import ResetPasswordSVG from "../../../assets/images/reset-password-logo.svg";

interface ForgotPasswordModalProps {
  open: boolean;
  onClose: () => void;
}

const linkSx = {
  fontSize: "0.75rem",
  fontWeight: "500",
  color: Colors.BLACK_151517,
  cursor: "pointer",
  transition: "color 0.2s ease",
  "&:hover": { color: Colors.LAZO_PURPLE },
};

export function ForgotPasswordModal({ open, onClose }: ForgotPasswordModalProps) {
  const { t } = useTranslation("auth");

  const { control, register, handleSubmit, getValues, reset } = useForm();
  const { control: sentControl, register: sentRegister } = useForm();

  const forgotPasswordMutation = useMutation({
    mutationFn: (email: string) => LazoCustomAuthControllerV2Service.forgotPassword(email).then((r) => r.getContentOrThrowError()),
    onError: () => NotificationService.error(t("forgotPassword.errors.resetError")),
  });

  const sentToEmail = forgotPasswordMutation.isSuccess ? getValues("email") : null;

  function handleClose() {
    reset();
    forgotPasswordMutation.reset();
    onClose();
  }

  return (
    <LazoModal
      open={open}
      handleClose={handleClose}
      title={sentToEmail ? t("resetPasswordEmailSent.title") : t("forgotPassword.title")}
      sx={{ width: "26.75rem" }}
    >
      {sentToEmail ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem", alignItems: "center", textAlign: "center", pb: "0.5rem" }}>
          <img src={CheckYourEmailSVG.src} alt="check-your-email" style={{ width: "48px", height: "48px" }} />
          <Typography sx={{ fontSize: "0.875rem", fontWeight: "400", color: Colors.LIGHT_GRAY_8A8E93 }}>
            {t("resetPasswordEmailSent.subtitle")}
          </Typography>
          <ControlledEmailTextField
            defaultValue={sentToEmail}
            containerProps={{ sx: { width: "100%", textAlign: "start" } }}
            control={sentControl}
            register={sentRegister}
          />
          {forgotPasswordMutation.isPending ? (
            <Spinner variant="indeterminate" sx={{ color: Colors.LAZO_PURPLE }} />
          ) : (
            <Link onClick={() => forgotPasswordMutation.mutate(sentToEmail)} underline="hover" sx={linkSx}>
              {t("resetPasswordEmailSent.resendInstructions")}
            </Link>
          )}
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem", alignItems: "center", pb: "0.5rem" }}>
          <img src={ResetPasswordSVG.src} alt="reset-password" style={{ width: "48px", height: "48px" }} />
          <Typography sx={{ fontSize: "0.875rem", fontWeight: "400", color: Colors.LIGHT_GRAY_8A8E93, textAlign: "center" }}>
            {t("forgotPassword.subtitle")}
          </Typography>
          <ControlledEmailTextField containerProps={{ sx: { width: "100%" } }} control={control} register={register} />
          <ForgottenPasswordActionButton
            disabled={forgotPasswordMutation.isPending}
            onClick={handleSubmit((data: { email: string }) => forgotPasswordMutation.mutate(data.email))}
          >
            {forgotPasswordMutation.isPending ? <Spinner variant="indeterminate" size={22} /> : t("forgotPassword.sendEmailButton")}
          </ForgottenPasswordActionButton>
        </Box>
      )}
    </LazoModal>
  );
}
